import { useEffect, useMemo, useState } from "react";
import { useData } from "../context/DataContext";
import api from "../lib/api";
import { toast } from "sonner";
import { Plus, Upload, ChevronDown, ChevronRight, Pencil, KeyRound } from "lucide-react";
import InfoTip from "../components/InfoTip";
import AddPersonDialog from "../components/personnel/AddPersonDialog";
import EditPersonDrawer from "../components/personnel/EditPersonDrawer";
import BulkUploadDialog from "../components/personnel/BulkUploadDialog";
import PasswordDialog from "../components/PasswordDialog";
import { TIERS, STATUSES, tierAverages, firmAverages, getTiers } from "../lib/personnel";
import { fmtMoney } from "../lib/format";

// Mirrors backend _email_for_name — used to match a person to their login.
const emailForName = (name) =>
  (name || "").toLowerCase().replace(/[^a-z\- ]/g, "").trim().split(/\s+/).join(".") + "@monkmackenzie.com";

export default function Personnel() {
  const { data, rawData, refresh } = useData();
  // Stable identity — see note in SubmissionsWidget: an inline `|| []` breaks
  // the production build via react-hooks/exhaustive-deps.
  const personnel = useMemo(() => data?.personnel || [], [data]);
  const [addOpen, setAddOpen] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [showDeparted, setShowDeparted] = useState(false);
  const [users, setUsers] = useState([]);
  const [secret, setSecret] = useState(null);
  const [togglingId, setTogglingId] = useState(null);

  // Login accounts — so admins can see & toggle access per person.
  const loadUsers = async () => {
    try {
      const { data: u } = await api.get("/users");
      setUsers(u);
    } catch (error) {
      // Non-admin or transient — chips just won't show. Log so a real
      // outage doesn't disappear silently on a director's console.
      console.error("Personnel · loadUsers failed", error);
    }
  };
  useEffect(() => { loadUsers(); }, []);

  const userByEmail = useMemo(
    () => Object.fromEntries(users.map((u) => [u.email, u])), [users]);
  const loginFor = (person) => userByEmail[emailForName(person.fullName)] || null;

  const toggleAccess = async (person, enable) => {
    setTogglingId(person.id);
    try {
      const { data: res } = await api.post(`/personnel/${encodeURIComponent(person.id)}/access`, { enable });
      if (res.password) {
        setSecret({ email: res.user.email, password: res.password, title: "Login created" });
      } else {
        toast.success(enable ? `${person.fullName}'s login re-enabled` : `${person.fullName}'s login disabled`);
      }
      await loadUsers();
      if (res.created) await refresh(); // a principal entity may have been added
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not update access");
    } finally {
      setTogglingId(null);
    }
  };

  // Group by tier, preserving the canonical order + admin-added ones.
  const allTiers = useMemo(() => getTiers(rawData), [rawData]);
  const grouped = useMemo(() => {
    const map = Object.fromEntries(allTiers.map((t) => [t, []]));
    for (const p of personnel) {
      if (p.status === "departed") continue;
      if (!map[p.tier]) map[p.tier] = [];   // handle stale tier gracefully
      map[p.tier].push(p);
    }
    return map;
  }, [personnel, allTiers]);

  const departed = useMemo(
    () => personnel.filter((p) => p.status === "departed"),
    [personnel],
  );
  const averages = useMemo(() => tierAverages(personnel, allTiers), [personnel, allTiers]);
  const firmAvg = useMemo(() => firmAverages(personnel), [personnel]);

  const handleSaved = async () => {
    await refresh();
  };

  return (
    <div className="space-y-8" data-testid="personnel-page">
      <div className="flex items-end justify-between">
        <div>
          <div className="eyebrow">Source of truth · admin-only</div>
          <h2 className="section-h mt-1 inline-flex items-center">
            Personnel · {personnel.filter((p) => p.status !== "departed").length} active
            <InfoTip side="right" label="What is this?">
              Master list of every person at the firm + what they cost. Tier
              averages computed here will eventually replace the flat
              ${20000}/month driver rate when a principal picks a tier rather
              than a named person.
            </InfoTip>
          </h2>
        </div>
        <div className="flex gap-2">
          <button
            onClick={async () => {
              const name = window.prompt("New tier name (e.g. Graduate):");
              if (!name || !name.trim()) return;
              try {
                await api.post("/tiers", { name: name.trim() });
                await refresh();
                toast.success(`Tier "${name.trim()}" added`);
              } catch (e) {
                toast.error(e.response?.data?.detail || "Could not add tier");
              }
            }}
            className="inline-flex items-center gap-2 px-3 py-2 border border-[var(--rule)] text-xs font-display tracked-tight uppercase hover:bg-[var(--paper-soft)]"
            data-testid="add-tier-button"
            title="Create a custom tier (e.g. Graduate). Appears in every tier picker after the system five."
          >
            + Add tier
          </button>
          <button
            onClick={() => setUploadOpen(true)}
            className="inline-flex items-center gap-2 px-3 py-2 border border-[var(--rule-strong)] text-xs font-display tracked-tight uppercase hover:bg-[var(--paper-soft)]"
            data-testid="bulk-upload-button"
          >
            <Upload size={12} /> Bulk upload
          </button>
          <button
            onClick={() => setAddOpen(true)}
            className="inline-flex items-center gap-2 px-3 py-2 bg-[var(--ink)] text-white text-xs font-display tracked-tight uppercase"
            data-testid="add-person-button"
          >
            <Plus size={12} /> Add person
          </button>
        </div>
      </div>

      {/* Firm-average charge-out rates — across every active person */}
      {personnel.length > 0 && (
        <section className="border border-[var(--rule)]" data-testid="firm-average-strip">
          <div className="grid grid-cols-1 md:grid-cols-3 divide-y md:divide-y-0 md:divide-x divide-[var(--rule)]">
            <div className="kpi">
              <div className="kpi-label inline-flex items-center">
                Firm avg / month
                <InfoTip side="bottom" label="Firm-average monthly charge-out">
                  Average monthly charge-out rate across every <strong>active</strong> person, all tiers pooled. Departed people are not counted.
                </InfoTip>
              </div>
              <div className="kpi-value" data-testid="firm-avg-monthly">
                {firmAvg.count ? fmtMoney(firmAvg.avgMonthly) : "—"}
              </div>
            </div>
            <div className="kpi">
              <div className="kpi-label inline-flex items-center">
                Firm avg / hour
                <InfoTip side="bottom" label="Firm-average hourly charge-out">
                  Average hourly charge-out rate across every active person. Useful as the default rate for unassigned or time-charge work.
                </InfoTip>
              </div>
              <div className="kpi-value" data-testid="firm-avg-hourly">
                {firmAvg.count ? `$${firmAvg.avgHourly.toFixed(2)}` : "—"}
              </div>
            </div>
            <div className="kpi">
              <div className="kpi-label">Active people</div>
              <div className="kpi-value" data-testid="firm-avg-count">{firmAvg.count}</div>
            </div>
          </div>
        </section>
      )}

      {personnel.length === 0 && (
        <div className="border border-[var(--rule)] p-10 text-center" data-testid="personnel-empty">
          <div className="eyebrow text-[var(--soft-fg)] mb-2">No personnel yet</div>
          <div className="text-sm text-[var(--soft-fg)] max-w-md mx-auto">
            Add people one at a time or bulk-upload your rate card.
            Columns expected: Full Name · Tier · Monthly Rate ·
            Hourly Rate · Status.
          </div>
        </div>
      )}

      {/* Tier sections in canonical order */}
      {personnel.length > 0 && allTiers.map((tier) => {
        const rows = grouped[tier] || [];
        const avg = averages[tier] || { count: 0, avgMonthly: 0, avgHourly: 0 };
        return (
          <section key={tier} data-testid={`tier-section-${tier.replace(/ /g, "-").toLowerCase()}`}>
            <div className="flex items-baseline justify-between mb-3 pb-2 border-b border-[var(--rule)]">
              <div className="flex items-baseline gap-3">
                <h3 className="font-display text-lg tracked-tight">{tier}</h3>
                <span className="eyebrow">{rows.length} active</span>
              </div>
              <div className="flex items-center gap-6 text-xs font-mono-num">
                <div>
                  <span className="eyebrow mr-2">Avg / month</span>
                  <span data-testid={`avg-monthly-${tier.replace(/ /g, "-").toLowerCase()}`}>
                    {avg.count ? fmtMoney(avg.avgMonthly) : "—"}
                  </span>
                </div>
                <div>
                  <span className="eyebrow mr-2">Avg / hour</span>
                  <span data-testid={`avg-hourly-${tier.replace(/ /g, "-").toLowerCase()}`}>
                    {avg.count ? `$${avg.avgHourly.toFixed(2)}` : "—"}
                  </span>
                </div>
              </div>
            </div>

            {rows.length === 0 ? (
              <div className="text-xs text-[var(--soft-fg)] italic pl-1 py-2">No one in this tier yet.</div>
            ) : (
              <div>
                {rows.map((p) => (
                  <PersonRow key={p.id} person={p} onClick={() => setEditing(p)}
                    login={loginFor(p)} busy={togglingId === p.id}
                    onToggleAccess={toggleAccess} />
                ))}
              </div>
            )}
          </section>
        );
      })}

      {/* Departed (collapsed) */}
      {departed.length > 0 && (
        <section className="pt-4 border-t border-[var(--rule)]" data-testid="departed-section">
          <button
            onClick={() => setShowDeparted((v) => !v)}
            className="inline-flex items-center gap-2 eyebrow hover:text-[var(--ink)]"
            data-testid="departed-toggle"
          >
            {showDeparted ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
            {departed.length} departed
          </button>
          {showDeparted && (
            <div className="mt-3">
              {departed.map((p) => (
                <PersonRow key={p.id} person={p} onClick={() => setEditing(p)} />
              ))}
            </div>
          )}
        </section>
      )}

      <AddPersonDialog open={addOpen} onClose={() => setAddOpen(false)} onSaved={handleSaved} />
      <BulkUploadDialog open={uploadOpen} onClose={() => setUploadOpen(false)} onSaved={handleSaved} />
      {secret && <PasswordDialog secret={secret} onClose={() => setSecret(null)} />}
      <EditPersonDrawer
        open={!!editing}
        person={editing}
        onClose={() => setEditing(null)}
        onSaved={handleSaved}
        projects={data?.projects || []}
        months={data?.meta?.months || []}
        login={editing ? loginFor(editing) : null}
        onCreateAccount={(p) => toggleAccess(p, true)}
      />
    </div>
  );
}

function PersonRow({ person, onClick, login, busy, onToggleAccess }) {
  const statusChip =
    person.status === "active" ? "chip-yes"
    : person.status === "on leave" ? "chip-tentative"
    : "chip-no";
  const isAdmin = login?.role === "admin";
  const active = login && login.status !== "disabled";
  return (
    <div
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === "Enter") onClick(); }}
      className="w-full grid grid-cols-12 gap-3 py-2.5 px-3 text-sm border-b border-[var(--rule)]/60 hover:bg-[var(--paper-soft)] text-left items-center cursor-pointer"
      data-testid={`person-row-${person.id}`}
    >
      <div className="col-span-4 text-[var(--ink)]">
        {person.fullName}
      </div>
      <div className="col-span-2 font-mono-num text-right">{person.monthlyRate ? fmtMoney(person.monthlyRate) : "—"}</div>
      <div className="col-span-1 font-mono-num text-right">{person.hourlyRate ? `$${Number(person.hourlyRate).toFixed(2)}` : "—"}</div>
      <div className="col-span-2"><span className={`chip ${statusChip}`}>{person.status}</span></div>
      {/* Login access — toggle grants a portfolio login (password shown once).
          Admin accounts are managed on the Users page. */}
      <div className="col-span-2 flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
        {isAdmin ? (
          <span className="chip chip-yes inline-flex items-center gap-1" data-testid={`login-chip-${person.id}`}>
            <KeyRound size={9} /> admin
          </span>
        ) : (
          <>
            <button
              onClick={() => onToggleAccess(person, !active)}
              disabled={busy || person.status === "departed"}
              className={`relative w-9 h-5 rounded-full transition-colors disabled:opacity-40 ${active ? "bg-[var(--ink)]" : "bg-[#D4D4CC]"}`}
              title={active ? "Disable login access" : "Grant login access (portfolio account)"}
              data-testid={`access-toggle-${person.id}`}
              aria-pressed={active}
            >
              <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-all ${active ? "left-[18px]" : "left-0.5"}`} />
            </button>
            <span className={`text-[10px] font-mono-num uppercase tracking-wider ${active ? "text-[#2C4A38]" : "text-[var(--soft-fg)]"}`}
                  data-testid={`login-chip-${person.id}`}>
              {active ? "login on" : login ? "disabled" : "no login"}
            </span>
          </>
        )}
      </div>
      <div className="col-span-1 text-right text-[var(--soft-fg)]"><Pencil size={12} /></div>
    </div>
  );
}
